import { Component, Input, ViewChild, inject } from '@angular/core';
import { NgIf } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ModalDialogComponent, ModalDialogModule } from '@vg-constellation/angular-20/modal-dialog';
import { LinkModule } from '@vg-constellation/angular-20/link';
import { environment } from '../../../environments/environment';

// Only Vanguard-owned https hosts may be embedded in the legal forms iframe.
const LEGAL_FORMS_ALLOWED_HOST_SUFFIX = '.vanguard.com';

// Mirrors OwnAccountsService.noAgentsAssigned. Legal forms are generated by grants,
// so an account with no grantees has nothing for the MFE to return — the button is
// disabled instead of opening a pop-up that would render blank.
const NO_AGENTS_ASSIGNED = 0;

const LEGAL_FORMS_NO_GRANTEE_MESSAGE = 'No valid grantee found';

/**
 * Internal-only "View legal forms" button and the modal that embeds the legal forms MFE.
 * Shared by the own-account tiles in both src/app and src/app-multi-account, so the
 * feature behaves the same whichever app the MULTI_ACCOUNT toggle boots. The internal
 * gate lives at the call site so external tiles get no extra element in their layout.
 */
@Component({
    selector: 'zci-legal-forms',
    templateUrl: './legal-forms.component.html',
    styleUrls: ['./legal-forms.component.scss'],
    imports: [NgIf, ModalDialogModule, LinkModule],
})
export class LegalFormsComponent {
    private readonly sanitizer = inject(DomSanitizer);

    legalFormsUrl: SafeResourceUrl | null = null;

    @ViewChild('legalFormsModal') legalFormsModal: ModalDialogComponent;

    @Input() agentsCount: number;

    @Input() sagId: string;

    @Input() poid: string;

    @Input() heading: string = '';

    /**
     * True when the account has no grantees, in which case the MFE has no legal forms
     * to return. Covers an unbound agentsCount as well as an explicit zero.
     */
    get hasNoGrantees(): boolean {
        return (this.agentsCount ?? NO_AGENTS_ASSIGNED) === NO_AGENTS_ASSIGNED;
    }

    /** Hover text explaining why the legal forms button is disabled. */
    get legalFormsDisabledReason(): string | null {
        return this.hasNoGrantees ? LEGAL_FORMS_NO_GRANTEE_MESSAGE : null;
    }

    openLegalForms(): void {
        const url = this.buildLegalFormsUrl();

        if (!url) {
            return;
        }

        this.legalFormsUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        this.legalFormsModal?.openModalDialog();
    }

    onLegalFormsClose(): void {
        this.legalFormsUrl = null;
    }

    /**
     * Builds the legal forms MFE URL, or returns null when it cannot be built safely.
     * Query values are encoded by URLSearchParams and the configured host is checked
     * against the Vanguard allowlist before the caller bypasses Angular's URL sanitizer.
     */
    private buildLegalFormsUrl(): string | null {
        if (!environment.LEGAL_FORMS_MFE_URL || !this.sagId || !this.poid) {
            return null;
        }

        let url: URL;

        try {
            url = new URL(environment.LEGAL_FORMS_MFE_URL);
        } catch {
            return null;
        }

        const isAllowedHost =
            url.protocol === 'https:' && url.hostname.endsWith(LEGAL_FORMS_ALLOWED_HOST_SUFFIX);

        if (!isAllowedHost) {
            return null;
        }

        if (!url.pathname.endsWith('/')) {
            url.pathname += '/';
        }

        url.search = new URLSearchParams({
            context: 'zci',
            accountId: this.sagId,
            clientPoid: this.poid,
        }).toString();

        return url.toString();
    }
}
